import React, { useState, ReactElement } from 'react';
import BaseDialog from './BaseDialog';
import PrimaryButton from './PrimaryButton';

interface ConfirmDialogProps {
  open: boolean;
  onClose: () => void;
  title: string | ReactElement;
  dialogName?: string;
  maxWidth?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';
  onConfirm?: () => Promise<void> | void;
  onCancel?: () => void;
  confirmText?: string;
  cancelText?: string;
  buttonComponent?: React.ElementType;
  children?: React.ReactNode;
  disableSubmit?: boolean;
  submitTip?: string;
  confirmLoading?: boolean;
  confirmLoadingText?: string;
  cancelIsPrimary?: boolean;
  [key: string]: any; // Allow additional props
}

export default function ConfirmDialog({
  open,
  onClose = () => {},
  title,
  dialogName,
  maxWidth = 'sm',
  onConfirm = () => {},
  onCancel = () => {},
  confirmText = 'Confirm',
  cancelText = 'Cancel',
  buttonComponent = PrimaryButton,
  children,
  disableSubmit: disableSubmitProps = false,
  hideCloseButton: hideCancelButton = false,
  submitTip = '',
  confirmLoading = false,
  confirmLoadingText = '',
  cancelIsPrimary = false,
  ...rest
}: ConfirmDialogProps) {
  const [isSubmitting, setIsSubmitting] = useState(false);

  const confirmAction = {
    [confirmText]: async () => {
      if (isSubmitting) {
        return;
      }
      setIsSubmitting(true);
      try {
        await onConfirm();
      } finally {
        setIsSubmitting(false);
      }
    },
  };

  const cancelAction = {
    [cancelText]: async () => {
      onCancel();
      onClose();
    },
  };

  const actions: { [key: string]: () => Promise<void> } = {
    ...confirmAction,
    ...(!hideCancelButton ? cancelAction : {}),
  };

  return (
    <BaseDialog
      open={open}
      onClose={onClose}
      maxWidth={maxWidth}
      title={title}
      actionButtonComponent={buttonComponent}
      dialogName={dialogName}
      actions={actions}
      primaryActionIndex={cancelIsPrimary && !hideCancelButton ? 1 : 0}
      disableSubmit={disableSubmitProps || isSubmitting || confirmLoading}
      submitLoading={confirmLoading || isSubmitting}
      submitLoadingText={confirmLoadingText || confirmText}
      submitTip={submitTip}
      {...rest}
    >
      {children}
    </BaseDialog>
  );
}
